/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Dynamic content", description = "Show different recipients different versions of an image, text, or button in one Lifecycle Studio email, based on customer data in your ESP.", lastUpdatedDate = new Date(1792627200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Dynamic content",
        description: "Show different recipients different versions of an image, text, or button in one Lifecycle Studio email, based on customer data in your ESP.",
        lastUpdatedDate: "Thu Oct 22 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "How dynamic content works",
          "depth": 1,
          "id": "how-dynamic-content-works"
        }, {
          "value": "Before you begin",
          "depth": 0,
          "id": "before-you-begin"
        }, {
          "value": "Choose what conditions check",
          "depth": 1,
          "id": "choose-what-conditions-check"
        }, {
          "value": "Decide what to make dynamic",
          "depth": 0,
          "id": "decide-what-to-make-dynamic"
        }, {
          "value": "Add dynamic content",
          "depth": 0,
          "id": "add-dynamic-content"
        }, {
          "value": "Preview, test, and export",
          "depth": 0,
          "id": "preview-test-and-export"
        }, {
          "value": "Test your variants in your ESP",
          "depth": 1,
          "id": "test-your-variants-in-your-esp"
        }, {
          "value": "Localize content for regions and languages",
          "depth": 0,
          "id": "localize-content-for-regions-and-languages"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      img: "img",
      h3: "h3",
      code: "code",
      ol: "ol"
    }, _provideComponents(), props.components), {FeatureFlag} = _components;
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    return _jsxs(_Fragment, {
      children: [_jsx(FeatureFlag, {
        featureName: "Dynamic content"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Lifecycle marketers, creative teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsx("li", {
                  children: _jsx(_components.a, {
                    href: "/lifecycle-studio/overview#get-started",
                    children: "Lifecycle Studio setup complete"
                  })
                }), _jsxs("li", {
                  children: ["An email service provider (ESP) set as your ", _jsx(_components.a, {
                    href: "/context-hub/connections#set-a-default-email-provider",
                    children: "default email provider"
                  })]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Dynamic content shows different recipients different versions of an image, text, or button in the same email, so you don't need a separate email for each audience."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Use dynamic content to change parts of an email based on customer data in your ESP, such as a customer's rewards tier, nearest store, or country. You create one email in Lifecycle Studio and choose which parts should vary. When your ESP sends the email, it uses each recipient's data to pick which version they see."
      }), "\n", _jsx(_components.p, {
        children: "For example, a credit card rewards email can change its hero image and button based on each customer's card tier:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Core cardholders"
          }), " see a hero image promoting 1.5% back on every purchase."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Plus cardholders"
          }), " see a hero image promoting 5% back on up to $1,500 each quarter."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Premier and Reserve cardholders"
          }), " each see their own hero image, along with a ", _jsx(_components.strong, {
            children: "See your points"
          }), " button."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Everyone else"
          }), " sees a general hero image and a ", _jsx(_components.strong, {
            children: "Learn more"
          }), " button."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Every recipient gets the same email, but the hero image and button change with their card tier. The logo, layout, and footer stay the same."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "lifecycle-studio/dynamic-content/ls-dynamic-content-variants-example.png",
          alt: "The card rewards example: a hero image version for each card tier plus a fallback, and two button versions"
        })
      }), "\n", _jsx(_components.h3, {
        id: "how-dynamic-content-works",
        children: "How dynamic content works"
      }), "\n", _jsxs(_components.p, {
        children: ["Each part of an email that changes by recipient is a ", _jsx(_components.strong, {
          children: "dynamic element"
        }), ". Each version of that element is a ", _jsx(_components.strong, {
          children: "variant"
        }), ". Every variant has two parts:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Content"
          }), " — What the recipient sees, such as an image, text, or a button."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Logic"
          }), " — The condition that decides who sees the variant, such as \"the card tier is Premier or Reserve.\""]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["When your ESP sends the email, it checks a dynamic element's variants in order and shows each recipient the first one whose condition matches their data. Every dynamic element ends with an ", _jsx(_components.strong, {
          children: "Everyone else"
        }), " variant, which Lifecycle Studio adds for recipients who don't match any condition."]
      }), "\n", _jsx(_components.p, {
        children: "Lifecycle Studio creates the content and conditions, and your ESP applies them. The agent writes each condition in your ESP's template language, such as Liquid for Braze, so you don't write the template code yourself. When you export the email, the conditions go with it, and your ESP applies them when it sends."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "before-you-begin",
        children: "Before you begin"
      }), "\n", _jsxs(_components.p, {
        children: ["Dynamic content uses customer data from your ESP to decide which variant each recipient sees. For example, a rewards email might use a ", _jsx(_components.code, {
          children: "cardtype"
        }), " field to show different content by card tier."]
      }), "\n", _jsxs(_components.p, {
        children: ["Before you create dynamic content, give Hightouch access to your ESP's customer fields, so the agent writes conditions with the exact field names your ESP uses. If a condition uses a field name that doesn't exist in your ESP, it never matches. Recipients who should have matched it see the ", _jsx(_components.strong, {
          children: "Everyone else"
        }), " variant instead."]
      }), "\n", _jsx(_components.p, {
        children: "To make your ESP's fields available to the agent:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Turn on read access in your ESP."
            }), " Open the API key that Hightouch uses and turn on read access to custom attributes and content blocks. In Braze, for example, these permissions are ", _jsx(_components.code, {
              children: "custom_attributes.get"
            }), ", ", _jsx(_components.code, {
              children: "content_blocks.list"
            }), ", and ", _jsx(_components.code, {
              children: "content_blocks.info"
            }), ". Also confirm that the API endpoint matches your ESP account's region."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Test the destination connection."
            }), " Open your ESP ", _jsx(_components.a, {
              href: "/destinations/overview",
              children: "destination"
            }), " in Hightouch and select ", _jsx(_components.strong, {
              children: "Test connection"
            }), ". If the custom attributes test fails, update the destination with an API key that has the permissions from step 1."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Sync your personalization data."
            }), " Go to ", _jsx(_components.strong, {
              children: "Context Hub > Connected data"
            }), ". On the ", _jsx(_components.strong, {
              children: "Integrations"
            }), " tab, confirm your ESP is connected under ", _jsx(_components.strong, {
              children: "Email Service Providers"
            }), ". Then open the ", _jsx(_components.strong, {
              children: "Personalization data"
            }), " tab, select your ESP, and select ", _jsx(_components.strong, {
              children: "Resync"
            }), "."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "lifecycle-studio/dynamic-content/ls-dynamic-content-connected-data.png",
              alt: "Connected data Integrations tab with a connected ESP under Email Service Providers and the Personalization data tab"
            })
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Once the sync completes, the agent uses your ESP's field names when it writes conditions. For details on syncing fields and content blocks, see ", _jsx(_components.a, {
          href: "/context-hub/connections#personalization-data",
          children: "Personalization data"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-what-conditions-check",
        children: "Choose what conditions check"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, conditions check fields in your ESP's customer profiles. To change this, set ", _jsx(_components.strong, {
          children: "Conditional type"
        }), " on the ", _jsx(_components.strong, {
          children: "Personalization data"
        }), " tab:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "ESP"
          }), " — Conditions check fields in your ESP's customer profiles, and your ESP checks them when it sends the email. This is the default."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Hightouch"
          }), " — Conditions use audience definitions built on your warehouse data. Each time the send's sync runs, Hightouch checks which variant each recipient matches and syncs the result to your ESP. Your ESP then shows each recipient their matching variant. This option works with Braze, Iterable, and Salesforce Marketing Cloud."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["With ", _jsx(_components.strong, {
          children: "Hightouch"
        }), ", a recipient's variant reflects their data when the sync last ran, not when your ESP sends. Changes to a condition or audience apply on the next sync run."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "lifecycle-studio/dynamic-content/ls-dynamic-content-personalization-data.png",
          alt: "Personalization data tab with the workspace default ESP, Conditional type set to ESP, the connected providers list, and Resync"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "decide-what-to-make-dynamic",
        children: "Decide what to make dynamic"
      }), "\n", _jsx(_components.p, {
        children: "Before you create variants, decide which parts of your email change based on customer data. Keep content that's the same for everyone outside dynamic elements."
      }), "\n", _jsx(_components.p, {
        children: "For example, in the card rewards email:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Hero image"
          }), " — Changes for each card tier."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Button"
          }), " — Changes only for Premier and Reserve cardholders."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Logo, body copy, and footer"
          }), " — Stay the same for everyone."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The hero image and button change for different groups of recipients, so each is a separate dynamic element. You can then change the button text once, instead of in every hero variant."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "lifecycle-studio/dynamic-content/ls-dynamic-content-separate-blocks.png",
          alt: "The card rewards email with the hero image and button in separate dynamic elements, and the body copy shared by everyone"
        })
      }), "\n", _jsx(_components.p, {
        children: "When you choose what to make dynamic:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Select only the content that changes."
          }), " If one line of text varies, make that line dynamic instead of the whole section."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Separate elements that vary for different groups."
          }), " Make a hero image and a button separate dynamic elements when they change for different recipients."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Keep shared content outside variants."
          }), " Don't repeat logos, buttons, legal text, sign-offs, or footers in every variant."]
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "If an email already repeats content across variants, ask the agent to clean it up: \"Make only the parts that differ between variants dynamic. Move anything repeated, like buttons or footers, outside the variants. Don't change any copy.\""
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "add-dynamic-content",
        children: "Add dynamic content"
      }), "\n", _jsx(_components.p, {
        children: "Finish your message before you add dynamic content. Changes to the main message don't update variants you've already created."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Select the content you want to personalize."
            }), " In the email preview, select the element that should change by recipient, such as a hero image or product module. If the content has its own background, select the whole block so every variant keeps it."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Select Make dynamic."
            }), " The option appears in the toolbar for your selection."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "lifecycle-studio/dynamic-content/ls-dynamic-content-make-dynamic.png",
              alt: "A hero image selected in the email preview with the Make dynamic option in the selection toolbar"
            })
          }), "\n", _jsx(_components.p, {
            children: "If you select several elements, Lifecycle Studio makes the smallest section that contains all of them dynamic. Each variant replaces that whole section, including any content inside it that you didn't select."
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Set the number of variations."
            }), " Count only the groups that get their own version, not ", _jsx(_components.strong, {
              children: "Everyone else"
            }), ". For example, set it to ", _jsx(_components.code, {
              children: "4"
            }), " for the four card tiers. Lifecycle Studio adds ", _jsx(_components.strong, {
              children: "Everyone else"
            }), " as a fifth variant."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Describe what each variant should show and who should see it."
            }), " Name the customer field, for example, \"One version per card tier, based on cardtype. Show each tier's card art and its main reward.\" You can also upload a reference file."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "lifecycle-studio/dynamic-content/ls-dynamic-content-create-variant.png",
              alt: "Create variant dialog with the number of variations set to 4 and a description of how the image should vary"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Generate the variants."
            }), " Select ", _jsx(_components.strong, {
              children: "Generate"
            }), " to have the agent create the variants, or select ", _jsx(_components.strong, {
              children: "Skip and add manually"
            }), " to write them yourself. If you have unsaved edits, Lifecycle Studio saves them as a new version before it creates the variants."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Review each variant."
            }), " Variants appear in a row on the canvas, next to the email. Select the dynamic element to check each variant's content and condition. Changes to one variant don't affect the others, so to update content they share, edit each one."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "lifecycle-studio/dynamic-content/ls-dynamic-content-canvas-row.png",
              alt: "The email with its hero image linked to a row of variants on the canvas, one per card tier, ending with Everyone else"
            })
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "lifecycle-studio/dynamic-content/ls-dynamic-content-variants-panel.png",
              alt: "Variants panel listing a variant for each card tier, each with Content and Logic set, and the Everyone else fallback"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Check the Everyone else variant."
            }), " Make sure its content makes sense for anyone who doesn't match a condition, including recipients with no card tier on file."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Repeat for each element that changes separately."
            }), " For the card rewards button, select the button, set the number of variations to ", _jsx(_components.code, {
              children: "1"
            }), ", and describe the change. Lifecycle Studio adds ", _jsx(_components.strong, {
              children: "Everyone else"
            }), " as the second version."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "lifecycle-studio/dynamic-content/ls-dynamic-content-create-text-variant.png",
              alt: "Create variant dialog for a text element with one variation and a description of the button change"
            })
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "preview-test-and-export",
        children: "Preview, test, and export"
      }), "\n", _jsxs(_components.p, {
        children: ["Before you export, use ", _jsx(_components.strong, {
          children: "Preview dynamic content"
        }), " to see the email with each variant. Select a variant, such as ", _jsx(_components.strong, {
          children: "Core cardholders"
        }), ", to update the email preview. The panel also shows which ESP and template language the conditions use."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "lifecycle-studio/dynamic-content/ls-dynamic-content-preview.png",
          alt: "Preview dynamic content panel with Core cardholders selected and the email preview showing the Core hero image"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["When the email is ready, ", _jsx(_components.a, {
          href: "/lifecycle-studio/messages#export-a-message",
          children: "export it to your ESP"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "test-your-variants-in-your-esp",
        children: "Test your variants in your ESP"
      }), "\n", _jsx(_components.p, {
        children: "The Lifecycle Studio preview shows each variant, but only your ESP shows which variant a real profile gets. In your ESP, preview the email with test profiles that match different conditions. For the card rewards example, test a Core cardholder, a Reserve cardholder, and someone with no card tier on file."
      }), "\n", _jsx(_components.p, {
        children: "Before you send, check that:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Each test profile sees its own variant. If a profile that should match a condition sees ", _jsx(_components.strong, {
            children: "Everyone else"
          }), ", check that the field name and value in the condition match the profile's data in your ESP."]
        }), "\n", _jsxs(_components.li, {
          children: ["Recipients who don't match any condition see the ", _jsx(_components.strong, {
            children: "Everyone else"
          }), " variant."]
        }), "\n", _jsx(_components.li, {
          children: "Shared content, such as the logo and footer, looks the same in every variant."
        }), "\n", _jsx(_components.li, {
          children: "Long product names, tier names, or translated text don't break the layout."
        }), "\n", _jsx(_components.li, {
          children: "Variants render correctly in the email clients your recipients use, such as Gmail and Outlook."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "If you made the same fix in more than one email, save it as a rule in Context Hub so the agent applies it in future emails."
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Where design matters, build the variant as an image. Text-based variants can pick up styling from the section around them, such as a background color. Building designed pieces like hero cards as images keeps every variant consistent."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "localize-content-for-regions-and-languages",
        children: "Localize content for regions and languages"
      }), "\n", _jsx(_components.p, {
        children: "You can also use dynamic content to show different languages or regional content in the same email. For example, recipients in France see French copy, and recipients in Spain see Spanish copy."
      }), "\n", _jsx(_components.p, {
        children: "Save your markets, languages, and translation rules in a Context Hub file, so you don't have to list them in every prompt. When you ask for localized variants, the agent uses the markets in the file. To cover a new market, add it to the file."
      }), "\n", _jsx(_components.p, {
        children: "You can ask the Context Hub agent to create the file. Include:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Markets and languages"
          }), " — The language each market uses, including regional versions such as Latin American Spanish and European Spanish."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Terms to leave untranslated"
          }), " — Brand names, product names, and tier names that stay in their original language."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Content your team translates"
          }), " — Content such as legal disclosures, where the agent must use your team's approved translations instead of translating it."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "lifecycle-studio/dynamic-content/ls-dynamic-content-context-hub-markets.png",
          alt: "Context Hub chat proposing a context file of email markets and protected terms for review"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Review and approve the file the agent suggests. The saved file appears with your other files in ", _jsx(_components.strong, {
          children: "Context Hub > Knowledge > Files"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "lifecycle-studio/dynamic-content/ls-dynamic-content-knowledge-files.png",
          alt: "Knowledge Files list with a localization rules file and a disclosures file"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Open the file to update its rules, or to ", _jsx(_components.a, {
          href: "/context-hub/business-context#choose-where-agents-use-context",
          children: "choose which agents use it and when"
        }), ". ", _jsx(_components.strong, {
          children: "Usage guidance"
        }), " tells agents when to read the file."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "lifecycle-studio/dynamic-content/ls-dynamic-content-localization-file.png",
          alt: "A saved localization file with its usage guidance and the rule content"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/lifecycle-studio/campaigns#launch",
            children: "Launch a campaign"
          }), " to pair the email with an audience and sync it to your ESP."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/context-hub/connections#personalization-data",
            children: "Personalization data"
          }), " to manage the fields and content blocks the agent writes conditions against."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
